import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  TextField,
} from '@mui/material';
import { Check, Warning, CheckCircle, Star } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { trackEvent, AnalyticsEvents } from '../../services/analytics';

type PlanKey = 'free' | 'family_monthly' | 'family_yearly';

interface PlanConfig {
  name: string;
  price: string;
  duration: string;
  period: string;
  priceId: string | null;
  features: string[];
  recommended?: boolean;
  savings?: string;
}

const Membership: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const membership = user?.membership;

  // State
  const [loading, setLoading] = useState<PlanKey | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    description: string;
    stripe_promotion_code_id?: string;
    source: 'db' | 'stripe';
  } | null>(null);

  const validatePromoCode = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoValidating(true);
    setPromoError(null);
    try {
      const { data } = await api.post('/promo-codes/validate', { code });
      if (!data.valid) {
        setPromoError(data.error || 'Invalid promo code');
        setAppliedPromo(null);
      } else {
        let description: string;
        if (data.source === 'stripe') {
          description = data.description;
        } else {
          // DB code — will grant comp Premium when user selects Free
          const days = data.trial_days || 30;
          const creatorName = data.creator?.name || data.chef?.name;
          description = creatorName
            ? `${days} days of Premium free courtesy of ${creatorName}`
            : `${days} days of Premium free`;
        }
        setAppliedPromo({
          code: data.code,
          description,
          stripe_promotion_code_id: data.stripe_promotion_code_id,
          source: data.source,
        });
      }
    } catch (err: any) {
      setPromoError(err.response?.data?.error || 'Failed to validate code');
      setAppliedPromo(null);
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromo = () => {
    setPromoInput('');
    setAppliedPromo(null);
    setPromoError(null);
  };

  // Track paywall view on mount
  useEffect(() => {
    trackEvent(AnalyticsEvents.PAYWALL_VIEWED);
  }, []);

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    const handleSuccess = async () => {
      trackEvent(AnalyticsEvents.SUBSCRIPTION_STARTED, { platform: 'web', provider: 'stripe' });
      await refreshUser(); // Refresh user data to get updated membership

      // Check if we came from approval flow
      const pendingPlanId = localStorage.getItem('pendingApprovalPlanId');
      if (pendingPlanId) {
        localStorage.removeItem('pendingApprovalPlanId');
        setSuccessMessage('Payment successful! Redirecting to approve your meal plan...');
        setTimeout(() => {
          navigate(`/meal-plans/${pendingPlanId}/review?autoApprove=true`);
        }, 1500);
      } else {
        setSuccessMessage('Payment successful! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    };

    if (searchParams.get('success') === 'true') {
      handleSuccess();
    } else if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled. You can try again when ready.');
    }
  }, [searchParams, refreshUser, navigate]);

  // Program membership detection
  const isProgramMember = !!(user?.creatorProgram || user?.chefProgram);

  // Determine current status.
  // Our internal statuses are: onboarding, free, paid, lapsed, cancelled, pending_migration.
  // Stripe's 'trialing'/'active' both map to our 'paid' on inbound webhook (stripeService.js),
  // so 'paid' is the single source of truth for "user has a Premium subscription right now."
  const isExpired = !user?.membershipActive;
  const hadTrial = membership?.trial_end_date != null;
  const isPaid = membership?.status === 'paid';
  // Real trial = currently paid AND trial_end_date is still in the future.
  const isTrialing =
    isPaid && !!membership?.trial_end_date && new Date(membership.trial_end_date) > new Date();
  // "Cancelled" = explicit cancel, OR a previously-paid sub with auto_renew off (Stripe cancel,
  // user still has access until end_date). Free users have auto_renew=false by design and
  // must NOT be flagged as cancelled.
  const isCancelled =
    membership?.status === 'cancelled' ||
    (!isProgramMember && isPaid && membership?.auto_renew === false);
  const isFreeTier = membership?.status === 'free';
  const hasActiveSubscription = isPaid; // Has a Premium subscription (trialing or fully active)

  // Price IDs from environment
  const PRICE_IDS = {
    family_monthly: process.env.REACT_APP_STRIPE_PRICE_FAMILY_MONTHLY || null,
    family_yearly: process.env.REACT_APP_STRIPE_PRICE_FAMILY_YEARLY || null,
  };

  const plans: Record<PlanKey, PlanConfig> = {
    free: {
      name: 'Free',
      price: '$0',
      duration: 'forever',
      period: 'free',
      priceId: null,
      features: [
        'Browse our full recipe library',
        'Manually build your meal plan',
        'Shopping list with categories',
        'Save favorites & want-to-try recipes',
        'Up to 6 family members',
      ],
    },
    family_monthly: {
      name: 'Monthly',
      price: '$7.99',
      duration: 'per month',
      period: 'month',
      priceId: PRICE_IDS.family_monthly,
      recommended: true,
      features: [
        'Personalized meal planning',
        'Smart shopping lists',
        'Up to 6 family members',
        'Dietary restrictions & allergies',
        'Recipe favorites & dislikes',
        'Cooking mode with step-by-step',
      ],
    },
    family_yearly: {
      name: 'Yearly',
      price: '$79.99',
      duration: 'per year',
      period: 'year',
      priceId: PRICE_IDS.family_yearly,
      savings: 'Save 17%',
      features: [
        'All features included',
        '2 months free',
        'Best value for families',
      ],
    },
  };

  const handleSubscribe = async (planKey: PlanKey) => {
    // Free tier path
    if (planKey === 'free') {
      setLoading('free');
      setError(null);
      try {
        // Free + Stripe code: reject, keep them on the page
        if (appliedPromo?.source === 'stripe') {
          setError(`The code ${appliedPromo.code} is a discount on a paid plan. Please select Monthly or Yearly to use it.`);
          setLoading(null);
          return;
        }

        // Free + DB code: grant comp Premium
        if (appliedPromo?.source === 'db') {
          await api.post('/promo-codes/apply-comp-premium', { code: appliedPromo.code });
          await refreshUser();
          setSuccessMessage(`${appliedPromo.description} activated! Redirecting...`);
          setTimeout(() => navigate('/dashboard'), 1500);
          return;
        }

        // Free + no code: just start free tier
        await api.post('/memberships/start-free');
        await refreshUser();
        navigate('/dashboard');
      } catch (err: any) {
        console.error('Free tier error:', err);
        setError(err.response?.data?.error || 'Failed to start free tier. Please try again.');
        setLoading(null);
      }
      return;
    }

    // Paid tier path (monthly/yearly)
    const plan = plans[planKey];

    if (!plan.priceId) {
      setError('This plan is not yet available. Please try another plan or contact support.');
      return;
    }

    setLoading(planKey);
    setError(null);

    try {
      const response = await api.post('/subscriptions/create-checkout-session', {
        priceId: plan.priceId,
        successUrl: `${window.location.origin}/membership?success=true`,
        cancelUrl: `${window.location.origin}/membership?canceled=true`,
        stripePromotionCodeId: appliedPromo?.stripe_promotion_code_id,
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.message || 'Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError(null);

    try {
      const response = await api.post('/subscriptions/create-portal-session');

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (err: any) {
      console.error('Portal error:', err);
      setError(err.response?.data?.message || 'Failed to open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const isNewUser = !membership;

  // Program members (creators/chefs) get a special view
  if (isProgramMember && !isExpired) {
    const programLabel = user?.creatorProgram && user?.chefProgram
      ? 'Creator Program & Chef Collective'
      : user?.creatorProgram
        ? 'Creator Program'
        : 'Chef Collective';

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          Membership
        </Typography>

        <Paper sx={{ mt: 3, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Star color="primary" />
            <Typography variant="h6" color="success.main" fontWeight="bold">
              {programLabel} - Active
            </Typography>
          </Box>

          {membership?.end_date && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Valid until: <strong>{new Date(membership.end_date).toLocaleDateString()}</strong>
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No payment required — your membership is included with the program.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {user?.creatorProgram && (
              <Button variant="outlined" onClick={() => navigate('/creator-dashboard')}>
                Creator Dashboard
              </Button>
            )}
            {user?.chefProgram && (
              <Button variant="outlined" onClick={() => navigate('/chef-dashboard')}>
                Chef Dashboard
              </Button>
            )}
          </Box>
        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Questions? <a href="mailto:support@snackdapp.com">Contact Support</a>
          </Typography>
        </Box>
      </Container>
    );
  }

  // Program member whose membership expired
  if (isProgramMember && isExpired) {
    const programLabel = user?.creatorProgram ? 'Creator Program' : 'Chef Collective';

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            {programLabel} Membership Expired
          </Typography>
          <Typography variant="body2">
            Your complimentary membership has ended
            {membership?.end_date && <> on {new Date(membership.end_date).toLocaleDateString()}</>}.
            Choose a plan below to continue using Snack'd.
          </Typography>
        </Alert>

        <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          Choose Your Plan
        </Typography>

        {/* Plans Grid */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', mt: 3 }}>
          {(Object.entries(plans) as [PlanKey, PlanConfig][]).map(([key, plan]) => (
            <Box key={key} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.333% - 16px)' }, maxWidth: 320, minWidth: 260 }}>
              <Card
                raised={plan.recommended}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.recommended ? '2px solid' : '1px solid',
                  borderColor: plan.recommended ? 'primary.main' : 'divider',
                  position: 'relative',
                }}
              >
                {plan.recommended && (
                  <Chip label="MOST POPULAR" color="primary" size="small" sx={{ position: 'absolute', top: 12, right: 12 }} />
                )}
                {plan.savings && (
                  <Chip label={plan.savings} color="success" size="small" sx={{ position: 'absolute', top: 12, left: 12 }} />
                )}
                <CardContent sx={{ flexGrow: 1, pt: plan.recommended || plan.savings ? 5 : 2 }}>
                  <Typography variant="h6" gutterBottom>{plan.name}</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h4" component="span" fontWeight="bold">{plan.price}</Typography>
                    <Typography variant="body2" component="span" color="text.secondary"> /{plan.period}</Typography>
                  </Box>
                  <List dense disablePadding>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}><Check color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={plan.recommended ? 'contained' : 'outlined'}
                    onClick={() => handleSubscribe(key)}
                    disabled={loading !== null || (key !== 'free' && !plan.priceId)}
                  >
                    {loading === key ? <CircularProgress size={24} color="inherit" /> : (key === 'free' ? 'Continue with Free' : 'Subscribe Now')}
                  </Button>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Questions? <a href="mailto:support@snackdapp.com">Contact Support</a>
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" icon={<CheckCircle />} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Expired/New User Banner */}
      {(isExpired || isNewUser) && (
        <Alert severity={hadTrial ? 'info' : 'warning'} icon={hadTrial ? <CheckCircle /> : <Warning />} sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            {hadTrial ? 'Your Free Trial Has Ended' : 'Subscribe to Continue'}
          </Typography>
          <Typography variant="body2">
            {hadTrial
              ? 'We hope you\'ve enjoyed Snack\'d and cooked some great meals! Choose a plan below to keep the delicious recipes coming.'
              : 'Choose a plan below to get started with personalized meal planning.'}
          </Typography>
        </Alert>
      )}

      <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
        Choose Your Plan
      </Typography>
      <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 1 }}>
        Subscribe to continue using Snack'd and unlock all premium features.
      </Typography>
      {/* Promo Code Input */}
      <Box sx={{ maxWidth: 500, mx: 'auto', mb: 4, mt: 2 }}>
        {!appliedPromo ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Have a promo code?"
              placeholder="Enter code"
              fullWidth
              size="small"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') validatePromoCode(); }}
              disabled={promoValidating}
              error={!!promoError}
              helperText={promoError}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={validatePromoCode}
              disabled={!promoInput.trim() || promoValidating}
              sx={{
                height: 40,
                minWidth: 90,
                fontWeight: 'bold',
                boxShadow: 2,
              }}
            >
              {promoValidating ? <CircularProgress size={20} color="inherit" /> : 'Apply'}
            </Button>
          </Box>
        ) : (
          <Alert
            severity="success"
            action={
              <Button color="inherit" size="small" onClick={clearPromo}>
                Remove
              </Button>
            }
          >
            <strong>{appliedPromo.code}</strong> applied — {appliedPromo.description}
          </Alert>
        )}
      </Box>

      {/* Plans Grid - Two cards side by side */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
        {(Object.entries(plans) as [PlanKey, PlanConfig][]).map(([key, plan]) => (
          <Box key={key} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)' }, maxWidth: 350 }}>
            <Card
              raised={plan.recommended}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: plan.recommended ? '2px solid' : '1px solid',
                borderColor: plan.recommended ? 'primary.main' : 'divider',
                position: 'relative',
              }}
            >
              {plan.recommended && (
                <Chip
                  label="MOST POPULAR"
                  color="primary"
                  size="small"
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                />
              )}
              {plan.savings && (
                <Chip
                  label={plan.savings}
                  color="success"
                  size="small"
                  sx={{ position: 'absolute', top: 12, left: 12 }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, pt: plan.recommended || plan.savings ? 5 : 2 }}>
                <Typography variant="h6" gutterBottom>
                  {plan.name}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" component="span" fontWeight="bold">
                    {plan.price}
                  </Typography>
                  <Typography variant="body2" component="span" color="text.secondary">
                    {' '}/{plan.period}
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Check color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                {hasActiveSubscription && !isCancelled ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                  >
                    {portalLoading ? <CircularProgress size={24} color="inherit" /> : 'Manage Subscription'}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant={plan.recommended ? 'contained' : 'outlined'}
                    onClick={() => handleSubscribe(key)}
                    disabled={loading !== null || (key !== 'free' && !plan.priceId)}
                  >
                    {loading === key ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : key === 'free' ? (
                      'Continue with Free'
                    ) : isCancelled ? (
                      'Resubscribe'
                    ) : (
                      'Subscribe Now'
                    )}
                  </Button>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Current Status */}
      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Status
        </Typography>
        {isExpired ? (
          <Typography variant="body1" color="error">
            {isNewUser ? (
              'No active membership. Choose a plan above to get started.'
            ) : (
              <>
                Your membership has <strong>expired</strong>.
                {hadTrial && membership?.trial_end_date && (
                  <> Your free trial ended on {new Date(membership.trial_end_date).toLocaleDateString()}.</>
                )}
              </>
            )}
          </Typography>
        ) : (
          <>
            {isCancelled ? (
              <>
                <Typography variant="body1" color="warning.main" fontWeight="bold">
                  ⚠ Subscription Cancelled
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  You'll have access until: <strong>{new Date(membership?.end_date || membership?.trial_end_date || new Date()).toLocaleDateString()}</strong>
                  <br />
                  You won't be charged. Want to resubscribe? Choose a plan above.
                </Typography>
              </>
            ) : isFreeTier ? (
              <>
                <Typography variant="body1" color="text.primary" fontWeight="bold">
                  Free tier
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  You're on the free plan. Upgrade to Monthly or Yearly above to unlock personalized meal plans and recipe recommendations.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1" color="success.main" fontWeight="bold">
                  ✓ Family Plan - Active
                </Typography>
                {isTrialing && membership?.trial_end_date && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Free trial ends on: <strong>{new Date(membership.trial_end_date).toLocaleDateString()}</strong>
                    <br />
                    You won't be charged until your trial ends.
                  </Typography>
                )}
                {isPaid && !isTrialing && membership?.end_date && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Next billing date: <strong>{new Date(membership.end_date).toLocaleDateString()}</strong>
                  </Typography>
                )}
              </>
            )}
          </>
        )}
        {hasActiveSubscription && !isCancelled && (
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={handleManageBilling}
            disabled={portalLoading}
          >
            {portalLoading ? <CircularProgress size={20} /> : 'Manage Billing'}
          </Button>
        )}
        {hasActiveSubscription && !isCancelled && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Use "Manage Billing" to update payment method, change plans, or cancel subscription.
          </Typography>
        )}
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Questions? <a href="mailto:support@snackdapp.com">Contact Support</a>
        </Typography>
      </Box>
    </Container>
  );
};

export default Membership;
